/**
 * Generative cover palettes + patterns used by `BookCover` when a book has
 * no `book_cover` image URL. The choice is derived deterministically from
 * the book id/title so the same book always shows the same colour scheme.
 */

export type BookCoverPalette =
  | 'orange'
  | 'blue'
  | 'green'
  | 'purple'
  | 'rose'
  | 'teal'
  | 'ochre'
  | 'navy'
  | 'cream'
  | 'forest';

export type BookCoverPattern = 'stripes' | 'dots' | 'waves' | 'arches' | 'grid' | 'none';

export const COVER_PALETTES: BookCoverPalette[] = [
  'orange',
  'blue',
  'green',
  'purple',
  'rose',
  'teal',
  'ochre',
  'navy',
  'cream',
  'forest',
];

export const COVER_PATTERNS: BookCoverPattern[] = [
  'stripes',
  'dots',
  'waves',
  'arches',
  'grid',
  'none',
];

/** Two-stop 135deg gradients for each palette option. */
export const paletteGradients: Record<BookCoverPalette, string> = {
  orange: 'linear-gradient(135deg, #F39449 0%, #A6470E 100%)',
  blue: 'linear-gradient(135deg, #4AAACB 0%, #1F5D7A 100%)',
  green: 'linear-gradient(135deg, #4FB67E 0%, #1F6B3D 100%)',
  purple: 'linear-gradient(135deg, #A683C4 0%, #5B3D85 100%)',
  rose: 'linear-gradient(135deg, #EB7A9A 0%, #B03454 100%)',
  teal: 'linear-gradient(135deg, #46B4AB 0%, #1E6A66 100%)',
  ochre: 'linear-gradient(135deg, #E8B24B 0%, #8A5A0B 100%)',
  navy: 'linear-gradient(135deg, #3B4E80 0%, #14204A 100%)',
  cream: 'linear-gradient(135deg, #F1E7CF 0%, #C9A86A 100%)',
  forest: 'linear-gradient(135deg, #5E8D5E 0%, #254A2A 100%)',
};

/** Semi-transparent white overlays — composited on top of the gradient. */
export const patternOverlays: Record<BookCoverPattern, string> = {
  stripes: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.12) 0 10px, transparent 10px 20px)',
  dots: 'radial-gradient(rgba(255,255,255,0.16) 1.5px, transparent 2px) 0 0 / 12px 12px',
  waves: 'radial-gradient(ellipse at 50% 120%, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0) 60%)',
  arches: 'radial-gradient(circle at 50% 100%, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0) 60%)',
  grid: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px) 0 0 / 14px 14px, linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px) 0 0 / 14px 14px',
  none: 'none',
};

function stringHash(input: string): number {
  let hash = 0;
  for (let i = 0; i < input.length; i += 1) {
    hash = (hash * 31 + input.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

export function paletteForSeed(seed: string): BookCoverPalette {
  return COVER_PALETTES[stringHash(seed) % COVER_PALETTES.length]!;
}

export function patternForSeed(seed: string): BookCoverPattern {
  return COVER_PATTERNS[stringHash(seed + '::pattern') % COVER_PATTERNS.length]!;
}
